import { type ContractorDto, contractorsApi } from '../../services/api';
import { ApiError } from '../../services/core.api';
import { onLocaleChange, t } from '../../services/i18n';
import { toastService } from '../../services/toast';
import { confirmDialog } from '../shared/rc-confirm-dialog';
import { openContractorCreateDialog, openContractorEditDialog } from './contractor-dialogs';
import './rc-contractor-list-view';
import { RcContractorListView } from './rc-contractor-list-view';

const CONTRACTOR_EVENTS = ['add', 'edit', 'delete', 'filter-change'] as const;

export class RcContractorList extends HTMLElement {
  private contractors: ContractorDto[] = [];
  private searchQuery = '';
  private tradeFilter = '';
  private disposeLocaleChange?: () => void;

  async connectedCallback(): Promise<void> {
    this.innerHTML = `<div class="loading">${t('common.loading')}</div>`;
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    CONTRACTOR_EVENTS.forEach((eventName) => this.addEventListener(`contractor-${eventName}`, this.handleContractorEvent as EventListener));
    await this.loadData();
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
    CONTRACTOR_EVENTS.forEach((eventName) => this.removeEventListener(`contractor-${eventName}`, this.handleContractorEvent as EventListener));
  }

  private async loadData(): Promise<void> {
    try {
      this.contractors = await contractorsApi.getAll();
      this.render();
    } catch (error) {
      console.error('Failed to load contractors:', error);
      this.innerHTML = `<div class="empty-state">${t('contractor.loadError')}</div>`;
    }
  }

  private render(): void {
    this.innerHTML = '<rc-contractor-list-view></rc-contractor-list-view>';
    const view = this.querySelector('rc-contractor-list-view') as RcContractorListView | null;
    if (!view) return;
    view.contractors = this.getFilteredContractors();
    view.allContractors = this.contractors;
    view.searchQuery = this.searchQuery;
    view.tradeFilter = this.tradeFilter;
    view.render();
  }

  private getFilteredContractors(): ContractorDto[] {
    const search = this.searchQuery.trim().toLowerCase();
    return this.contractors.filter((contractor) => {
      const matchesTrade = !this.tradeFilter || (contractor.trade ?? '') === this.tradeFilter;
      const haystack = [
        contractor.name,
        contractor.trade,
        contractor.notes,
        contractor.email,
        contractor.phoneNumber,
      ].filter(Boolean).join(' ').toLowerCase();
      const matchesSearch = !search || haystack.includes(search);
      return matchesTrade && matchesSearch;
    });
  }

  private handleContractorEvent = async (event: Event): Promise<void> => {
    const detail = (event as CustomEvent<any>).detail ?? {};
    switch ((event as CustomEvent).type) {
      case 'contractor-add':
        openContractorCreateDialog(async () => this.loadData());
        return;
      case 'contractor-edit': {
        const contractor = this.contractors.find((c) => c.id === detail.id);
        if (contractor) openContractorEditDialog(contractor, async () => this.loadData());
        return;
      }
      case 'contractor-delete':
        if (detail.id) await this.handleDelete(detail.id);
        return;
      case 'contractor-filter-change':
        this.searchQuery = detail.searchQuery ?? '';
        this.tradeFilter = detail.tradeFilter ?? '';
        this.render();
        return;
      default:
        return;
    }
  };

  private async handleDelete(id: string): Promise<void> {
    if (!await confirmDialog(t('contractor.confirmDelete'))) return;
    try {
      await contractorsApi.delete(id);
      await this.loadData();
    } catch (error) {
      console.error('Failed to delete contractor:', error);
      toastService.error(error instanceof ApiError ? error.message : t('contractor.deleteError'));
    }
  }
}

if (!customElements.get('rc-contractor-list')) {
  customElements.define('rc-contractor-list', RcContractorList);
}
